/**
 * MelItemCard Component
 * Displays a single MEL item with all its details
 */

import React from 'react';
import { cn } from '@/utils/cn';
import type { MelItem } from '@/types/mel';
import { REPAIR_CATEGORIES } from '@/types/mel';
import {
  DocumentTextIcon,
  ClockIcon,
  WrenchScrewdriverIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';

interface MelItemCardProps {
  item: MelItem;
  score?: number;
  matchType?: 'exact' | 'partial' | 'semantic';
  isSelected?: boolean;
  onClick?: () => void;
  showDetails?: boolean;
  className?: string;
}

export const MelItemCard: React.FC<MelItemCardProps> = ({
  item,
  score,
  matchType,
  isSelected = false,
  onClick,
  showDetails = true,
  className,
}) => {
  const repairCat = REPAIR_CATEGORIES[item.repairCategory] || {
    label: item.repairCategory,
    days: null,
  };

  const getMatchBadge = () => {
    if (!matchType) return null;

    const badgeConfig = {
      exact: {
        label: 'Exact Match',
        colors: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      },
      partial: {
        label: 'Partial Match',
        colors: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      },
      semantic: {
        label: 'Semantic Match',
        colors: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
      },
    };

    const config = badgeConfig[matchType];

    return (
      <span className={cn('px-2 py-0.5 text-xs font-medium rounded', config.colors)}>
        {config.label}
        {score !== undefined && ` (${Math.round(score * 100)}%)`}
      </span>
    );
  };

  return (
    <div
      onClick={onClick}
      className={cn(
        'rounded-lg border transition-all duration-200',
        'bg-card',
        onClick && 'cursor-pointer hover:shadow-md hover:border-primary/50',
        isSelected && 'border-primary ring-2 ring-primary/20',
        !isSelected && 'border-border',
        className
      )}
    >
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-sm font-bold text-primary">
                {item.itemNumber}
              </span>
              <span className="text-foreground/40">|</span>
              <span className="text-xs text-foreground/60">
                ATA {item.ataChapter}
              </span>
              {getMatchBadge()}
            </div>
            <h3 className="mt-1 text-lg font-semibold text-foreground">
              {item.title}
            </h3>
          </div>

          {/* Repair Category Badge */}
          <div className="flex-shrink-0 text-right">
            <div className="inline-flex items-center gap-1 px-2 py-1 rounded bg-muted text-sm">
              <ClockIcon className="w-4 h-4 text-foreground/60" />
              <span className="font-medium text-foreground">{repairCat.label}</span>
            </div>
            {repairCat.days && (
              <p className="text-xs text-foreground/50 mt-1">
                {repairCat.days} calendar days
              </p>
            )}
          </div>
        </div>

        {/* Installed/Required */}
        <div className="mt-2 flex items-center gap-4 text-sm">
          <span className="text-foreground/60">
            <Cog6ToothIcon className="w-4 h-4 inline mr-1" />
            {item.numberInstalled} installed
          </span>
          <span className="text-foreground/60">
            {item.numberRequired} required
          </span>
        </div>
      </div>

      {/* Details */}
      {showDetails && (
        <div className="p-4 space-y-4">
          {/* Dispatch Remarks */}
          <div>
            <h4 className="flex items-center gap-2 text-sm font-medium text-foreground mb-2">
              <DocumentTextIcon className="w-4 h-4 text-foreground/60" />
              Dispatch Remarks
            </h4>
            <p className="text-sm text-foreground/80 whitespace-pre-wrap">
              {item.remarks || item.operationalProcedures || 'No dispatch remarks specified.'}
            </p>
          </div>

          {/* Maintenance Procedures */}
          {item.maintenanceProcedures && (
            <div>
              <h4 className="flex items-center gap-2 text-sm font-medium text-foreground mb-2">
                <WrenchScrewdriverIcon className="w-4 h-4 text-foreground/60" />
                Maintenance Procedures
              </h4>
              <p className="text-sm text-foreground/80 whitespace-pre-wrap">
                {item.maintenanceProcedures}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Compact version for list views
 */
export const MelItemCardCompact: React.FC<{
  item: MelItem;
  onClick?: () => void;
  className?: string;
}> = ({ item, onClick, className }) => {
  const repairCat = REPAIR_CATEGORIES[item.repairCategory];

  return (
    <div
      onClick={onClick}
      className={cn(
        'flex items-center gap-4 p-3 rounded-lg border border-border',
        'bg-card transition-colors',
        onClick && 'cursor-pointer hover:bg-muted hover:border-primary/50',
        className
      )}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm font-bold text-primary">
            {item.itemNumber}
          </span>
          <span className="text-xs text-foreground/50">ATA {item.ataChapter}</span>
        </div>
        <p className="text-sm font-medium text-foreground truncate">{item.title}</p>
      </div>
      <div className="flex-shrink-0 text-right">
        <span className="text-xs font-medium text-foreground/60">
          {repairCat?.label || item.repairCategory}
        </span>
      </div>
    </div>
  );
};

export default MelItemCard;
